import { Form, FormOptions, Section, SelectOption, useNotify } from '@globaleris/ui.library'
import { Box, makeStyles } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity } from '../../../../api'
import {
  ConceptLayoutConfigurationModel,
  NewConceptPrice
} from '../../../../api/Argentia/endpoints/concepts/model/concepts'
import { EditablePriceModel } from '../../../../api/Argentia/model/editablePriceModel'
import { EnumPriceType } from '../../../../api/oData/enums/enumPriceType'
import { PriceExpressionValidator } from '../../../../components'
import { CardActionProps } from '../../../../components/shared/cardActions/cardActions'
import SimpleDialog from '../../../../components/shared/simpleDialog/simpleDialog'
import { getCustomerDomain, storeActivePrice } from '../../../../redux'
import {
  buildDiscriminantFormInputs,
  ConceptPriceDiscriminantField,
  hasAnyDiscriminantField
} from '../../concepts.helpers'
import { conceptPriceEditDialogStyles } from './conceptPriceEditDialog.styles'

const useStyles = makeStyles(conceptPriceEditDialogStyles)

interface ConceptPriceEditDialogProps {
  open: boolean
  onClose: (priceFields?: EditablePriceModel) => void
  priceType: EnumPriceType
  commodity: EnumCommodity
  priceData: EditablePriceModel | null
  isFetching?: boolean
  configuration: ConceptLayoutConfigurationModel
  existingPrices: NewConceptPrice[]
  isAddMode?: boolean
}

const initialPrice: EditablePriceModel = {
  priceCalculationType: 1,
  precioUnitario: 1,
  redondeo: 1,
  expression: ''
}

/**
 * Diálogo para editar precios asociados a conceptos.
 * Usa PriceExpressionValidator para editar la expresión del precio.
 */
export const ConceptPriceEditDialog: React.FC<ConceptPriceEditDialogProps> = ({
  open,
  onClose,
  priceType,
  commodity,
  priceData,
  isFetching = false,
  configuration,
  existingPrices,
  isAddMode = false
}) => {
  const [t] = useTranslation(['conceptsEdit', 'globalerisLibrary', 'enums'])
  const { info, error } = useNotify()
  const classes = useStyles()

  const [priceFields, setPriceFields] = useState<EditablePriceModel>(priceData ?? initialPrice)
  const [modifiedFields, setModifiedFields] = useState<boolean>(false)
  const [expressionIsValid, setExpressionIsValid] = useState<boolean>(true)
  const [isWaiting, setIsWaiting] = useState<boolean>(false)

  // Layout 4 discriminant states
  const [discriminantData, setDiscriminantData] = useState<{
    toll?: number | null
    usage?: number | null
    flow?: number | null
    duration?: number | null
    taxRegion?: number | null
    period?: number | null
  }>({})

  // Check if all required discriminant fields are filled
  const allRequiredFieldsFilled = useMemo(() => {
    //if (configuration.layout !== 4) return true

    const requiredFields: Array<{
      key: keyof typeof discriminantData
      isRequired: boolean
    }> = [
      { key: 'toll', isRequired: configuration.lgToll },
      { key: 'usage', isRequired: configuration.lgUsage },
      { key: 'flow', isRequired: configuration.lgUnitPriceFlow },
      { key: 'duration', isRequired: configuration.lgDuration },
      { key: 'taxRegion', isRequired: configuration.lgRegion },
      { key: 'period', isRequired: configuration.lgPeriod }
    ]

    return requiredFields.every(field => !field.isRequired || discriminantData[field.key] != null)
  }, [configuration, discriminantData])

  /**
   * Check if a specific combination of discriminants already exists in existingPrices
   * ONLY compares active discriminants (those enabled in configuration)
   * e.g., If only lgToll=true, then all records must have different toll values
   * e.g., If lgToll=true AND lgRegion=true, then records can share toll or region, but not both
   */
  const isCombinationDuplicate = (testCombination: typeof discriminantData): boolean => {
    return existingPrices.some(price => {
      // Compare ONLY the fields that are actually active (enabled in configuration)
      const compareActiveFields = () => {
        if (configuration.lgToll && testCombination.toll !== price.toll) return false
        if (configuration.lgUsage && testCombination.usage !== price.usage) return false
        if (configuration.lgUnitPriceFlow && testCombination.flow !== price.flow) return false
        if (configuration.lgDuration && testCombination.duration !== price.duration) return false
        if (configuration.lgRegion && testCombination.taxRegion !== price.taxRegion) return false
        if (configuration.lgPeriod && testCombination.period !== price.period) return false
        return true // All active fields match
      }

      return compareActiveFields()
    })
  }

  /**
   * Filter options to exclude only exact combinations that would create duplicates
   * Allows reusing field values in different combinations
   * In edit mode, always allows the current value even if it's technically a duplicate
   */
  const getFilteredOptions = (
    field: 'toll' | 'usage' | 'flow' | 'duration' | 'taxRegion' | 'period',
    allOptions: SelectOption[]
  ): SelectOption[] => {
    const currentValue = priceData?.[field]

    return allOptions.filter(opt => {
      // Create a test combination with the new value for this field
      const testCombination = {
        ...discriminantData,
        [field]: opt.value as number
      }

      // Check if this combination would be a duplicate
      const wouldBeDuplicate = isCombinationDuplicate(testCombination)

      // Allow the option if it wouldn't create a duplicate OR if it's the current value (for edit mode)
      return !wouldBeDuplicate || opt.value === currentValue
    })
  }

  // Price used by the versions page. Only available when editing an existing price.
  const versionsPrice = useMemo(() => {
    if (isAddMode || !priceData?.id) return undefined
    return {
      id: priceData.id,
      code: priceData.code,
      description: priceData.description,
      priceType,
      commodity,
      version: priceData.version
    }
  }, [isAddMode, priceData, priceType, commodity])

  // Force precioUnitario to 9 (Porcentaje) for layout 4
  const effectivePrecioUnitario = useMemo(() => {
    if (configuration.layout === 4) {
      return 9 // EnumPrecioUnitario.Porcentaje
    }
    return priceFields.precioUnitario
  }, [configuration.layout, priceFields.precioUnitario])

  // Form configuration for discriminant fields
  const formOptions: FormOptions = useMemo(
    () => ({
      columns: 1,
      elevation: 0,
      analytics: {
        customer: getCustomerDomain(),
        view: t('conceptsEdit:components.conceptPriceEditDialog.analytics')
      }
    }),
    [t]
  )

  const formSections: Section[] = useMemo(
    () => [
      {
        inputs: buildDiscriminantFormInputs(
          configuration,
          t,
          (field: ConceptPriceDiscriminantField, value: number) => {
            setDiscriminantData(prev => ({ ...prev, [field]: value }))
            setModifiedFields(true)
          },
          getFilteredOptions
        )
      }
    ],
    [configuration, t, isAddMode, existingPrices, priceData, discriminantData]
  )

  // #region Handlers
  const onDialogCloseHandler = () => {
    if (!modifiedFields) {
      onClose()
      return
    }

    // Include discriminants in priceFields, mapping from English to Spanish (OData Model used in Argentia endpoint (done before this code))
    const updatedPriceFields: EditablePriceModel = {
      ...priceFields,
      precioUnitario: effectivePrecioUnitario,
      ...(configuration.lgToll && { toll: discriminantData.toll }),
      ...(configuration.lgUsage && { usage: discriminantData.usage }),
      ...(configuration.lgUnitPriceFlow && { flow: discriminantData.flow }),
      ...(configuration.lgDuration && { duration: discriminantData.duration }),
      ...(configuration.lgRegion && { taxRegion: discriminantData.taxRegion }),
      ...(configuration.lgPeriod && { period: discriminantData.period })
    }

    setModifiedFields(false)
    onClose(updatedPriceFields)
  }
  // #endregion

  // #region Actions Definition
  const footerActions = useMemo<CardActionProps[]>(
    () => [
      {
        label: t('conceptsEdit:components.conceptPriceEditDialog.buttons.cancel'),
        hasPermission: true,
        handleActionClick: () => {
          onClose()
        },
        size: 'small',
        color: 'secondary',
        variant: 'text'
      },
      {
        label: t('conceptsEdit:components.conceptPriceEditDialog.buttons.validate'),
        hasPermission: true,
        handleActionClick: () => {}, // Fuerza trigger de onBlur event
        size: 'small',
        color: 'primary',
        variant: 'text',
        disabled: expressionIsValid
      },
      {
        label: t('conceptsEdit:components.conceptPriceEditDialog.buttons.save'),
        hasPermission: true,
        handleActionClick: onDialogCloseHandler,
        size: 'small',
        color: 'primary',
        variant: 'contained',
        disabled: isWaiting || !modifiedFields || !expressionIsValid || !allRequiredFieldsFilled
      }
    ],
    [
      t,
      onClose,
      expressionIsValid,
      onDialogCloseHandler,
      isWaiting,
      modifiedFields,
      allRequiredFieldsFilled
    ]
  )
  // #endregion

  // #region Hooks
  useEffect(() => {
    if (priceData) {
      setPriceFields(priceData)
      // Initialize discriminants from priceData (both edit and add mode)
      setDiscriminantData({
        toll: priceData.toll ?? null,
        usage: priceData.usage ?? null,
        flow: priceData.flow ?? null,
        duration: priceData.duration ?? null,
        taxRegion: priceData.taxRegion ?? null,
        period: priceData.period ?? null
      })
    }
  }, [priceData])

  useEffect(() => {
    if (open) {
      setModifiedFields(false)
      setExpressionIsValid(true)
      setIsWaiting(false)

      // Initialize discriminantData based on mode
      if (isAddMode) {
        // Reset discriminantData to null in add mode
        setDiscriminantData({
          toll: null,
          usage: null,
          flow: null,
          duration: null,
          taxRegion: null,
          period: null
        })
      } else if (priceData) {
        // Load discriminantData from priceData in edit mode
        setDiscriminantData({
          toll: priceData.toll ?? null,
          usage: priceData.usage ?? null,
          flow: priceData.flow ?? null,
          duration: priceData.duration ?? null,
          taxRegion: priceData.taxRegion ?? null,
          period: priceData.period ?? null
        })
      }
    }
  }, [open, isAddMode, priceData])

  // The versions page reads the active price from the store
  useEffect(() => {
    if (open && versionsPrice) {
      storeActivePrice(versionsPrice as any)
    }
  }, [open, versionsPrice])
  // #endregion

  return (
    <SimpleDialog
      open={open}
      handleClose={onDialogCloseHandler}
      footerActions={footerActions}
      title={t('conceptsEdit:components.conceptPriceEditDialog.title')}
    >
      {isFetching ? (
        <Skeleton height={200} style={{ margin: '0px', padding: '0px' }} />
      ) : (
        <>
          {/* discriminant fields */}
          {hasAnyDiscriminantField(configuration) && (
            <Box className={classes.dialogContent}>
              <Form
                title=""
                titleCustom={<></>}
                sections={formSections}
                options={formOptions}
                data={discriminantData}
              />
            </Box>
          )}

          <PriceExpressionValidator
            showPriceUnit
            elevation={0}
            disablePriceUnit={configuration.layout === 4} // Disable price unit selection for layout 4
            showRounding
            showVersion
            versionsButton
            show={true}
            priceType={priceType}
            commodity={commodity}
            activeExpressionIsValid={true}
            activeExpression={priceFields.expression!}
            activePriceCalculationType={priceFields.priceCalculationType!}
            activePriceUnit={effectivePrecioUnitario!}
            activeRounding={priceFields.redondeo!}
            onIsLoading={setIsWaiting}
            onSelectPriceUnit={value => {
              // For layout 4, precioUnitario is fixed to 9 (Porcentaje), ignore changes
              if (configuration.layout !== 4) {
                setPriceFields({ ...priceFields, precioUnitario: value })
                setModifiedFields(true)
              }
            }}
            onSelectRounding={value => {
              setPriceFields({ ...priceFields, redondeo: value })
              setModifiedFields(true)
            }}
            onSelectPriceCalculationType={value => {
              setPriceFields({ ...priceFields, priceCalculationType: value })
              setModifiedFields(true)
            }}
            onSelectValidExpression={value => {
              setPriceFields({ ...priceFields, expression: value })
              setModifiedFields(true)
            }}
            onIsValid={value => {
              setExpressionIsValid(value)
              setModifiedFields(true)
            }}
            onFocusExpressionInput={() => {
              setExpressionIsValid(false)
            }}
            expressionInputSize={12}
            priceUnitInputSize={5}
            price={versionsPrice}
          />
        </>
      )}
    </SimpleDialog>
  )
}
